/**
 * Pure selectors for the Work Items Dashboard page.
 *
 * All functions in this module are pure and referentially transparent — given
 * the same inputs (including the optional `now` parameter used to compute
 * `age`), they return equal outputs and perform no side effects.
 *
 * Sort is a PAGE-LOCAL, RENDER-TIME concern: `selectRenderedRows` reorders
 * only the rows visible on the current page and never mutates `pageCache` or
 * `forwardBuffer`.
 *   - Priority sort rank: HIGH=3, MEDIUM=2, LOW=1, unknown=0 (pinned last).
 *   - Age sort key: `lastWorkItemStatusUpdatedTime` (epoch ms). Older items
 *     have a smaller timestamp; "age desc" = oldest first.
 */

import { formatDuration } from "../../utils/formatDuration";
import {
    GetWorkItemCountsResponse,
    WorkItemRow,
    WorkItemsState,
    WorkItemSummary,
} from "./model";

// ---------------------------------------------------------------------------
// Row mapper: WorkItemSummary → WorkItemRow
// ---------------------------------------------------------------------------

// Field mapping:
//   severity                        → priorityLabel + priorityTagType + priorityRank
//   exceptionName                   → taskName
//   processPath                     → process
//   operator                        → startedBy  ("" when undefined)
//   lastWorkItemStatusUpdatedTime   → age = formatDuration(now − ts)
//                                     and the raw ts (for sort)
//
// `now` is exposed for testability; production callers default to Date.now().
export const toWorkItemRow = (
    summary: WorkItemSummary,
    now: number = Date.now(),
): WorkItemRow => {
    const { priorityLabel, priorityTagType, priorityRank } = mapSeverity(
        summary.severity,
    );

    // Future timestamps (a data anomaly) render with a leading minus so the
    // UI surfaces the issue rather than hiding it.
    const age = formatDuration(now - summary.lastWorkItemStatusUpdatedTime);

    return {
        id: summary.workItemId,
        priorityLabel,
        priorityTagType,
        priorityRank,
        taskName: summary.exceptionName,
        process: summary.processPath,
        startedBy: summary.operator ?? "",
        age,
        lastWorkItemStatusUpdatedTime: summary.lastWorkItemStatusUpdatedTime,
    };
};

// Any severity outside the known enum falls back to priorityTagType undefined
// and priorityRank 0, which pins unknown priorities to the bottom of a
// priority sort regardless of direction.
const mapSeverity = (
    severity: string | undefined,
): {
    priorityLabel: string;
    priorityTagType: WorkItemRow["priorityTagType"];
    priorityRank: number;
} => {
    switch (severity) {
        case "HIGH":
            return {
                priorityLabel: "High",
                priorityTagType: "error",
                priorityRank: 3,
            };
        case "MEDIUM":
            return {
                priorityLabel: "Medium",
                priorityTagType: "warning",
                priorityRank: 2,
            };
        case "LOW":
            return {
                priorityLabel: "Low",
                priorityTagType: "neutral",
                priorityRank: 1,
            };
        default:
            return {
                priorityLabel: severity ?? "—",
                priorityTagType: undefined,
                priorityRank: 0,
            };
    }
};

// ---------------------------------------------------------------------------
// Page-local sort
// ---------------------------------------------------------------------------

const applyPageLocalSort = (
    rows: WorkItemRow[],
    sort: WorkItemsState["sort"],
): WorkItemRow[] => {
    if (sort.column === null || sort.direction === null) {
        return rows;
    }

    // Never mutate the caller's array (pageCache items).
    const copy = rows.slice();
    const directionFactor = sort.direction === "asc" ? 1 : -1;

    if (sort.column === "priority") {
        copy.sort((a, b) => {
            // Unknown severity (rank 0) is pinned to the bottom so asc/desc
            // don't shuffle garbage values between ordered and unordered.
            const aUnknown = a.priorityRank === 0;
            const bUnknown = b.priorityRank === 0;
            if (aUnknown !== bUnknown) {
                return aUnknown ? 1 : -1;
            }
            return (a.priorityRank - b.priorityRank) * directionFactor;
        });
        return copy;
    }

    // "age" — older item = smaller timestamp. "asc" = youngest first.
    copy.sort((a, b) => {
        return (
            (b.lastWorkItemStatusUpdatedTime -
                a.lastWorkItemStatusUpdatedTime) *
            directionFactor
        );
    });
    return copy;
};

// ---------------------------------------------------------------------------
// State-level selectors
// ---------------------------------------------------------------------------

// Returns [] when no cache entry exists for the current page. Callers render
// loading / empty branches based on state.status.
export const selectRenderedRows = (
    state: WorkItemsState,
    now: number = Date.now(),
): WorkItemRow[] => {
    const cached = state.pageCache[state.currentPage] ?? [];
    const rows = cached.map((summary) => toWorkItemRow(summary, now));
    return applyPageLocalSort(rows, state.sort);
};

// One card per processPathCounts entry; `selected` is true only for the card
// whose name equals state.selectedProcessPath.
export const selectProcessPathCards = (
    state: WorkItemsState,
): Array<{ name: string; count: number; selected: boolean }> => {
    const counts: GetWorkItemCountsResponse | null = state.counts;
    if (counts === null) {
        return [];
    }

    return counts.processPathCounts.map((entry) => ({
        name: entry.processPath,
        count: entry.totalCount,
        selected: entry.processPath === state.selectedProcessPath,
    }));
};
